/**
 * O que faz: cartão "Leads para retomar hoje" no topo do Dashboard. Mostra quantos leads esperam resposta
 *   ou pararam de responder (pelo dia do calendário), quantos já foram retomados hoje e o resultado de ontem.
 * Onde usa: Dashboard (modos clínica e admin), logo abaixo do cabeçalho; não segue o período escolhido.
 * Conexões: useResumoRetomada, regras de features/retomada e RetomadaListaModal.
 * Tabelas: nenhuma direta; as contagens vêm da RPC get_dashboard_retomada_resumo_v1 (somente leitura, sem nome nem telefone).
 * Data: 22/10/2026.
 */
import { useState } from 'react';
import type { GrupoResumoRetomada } from '@/services/retomadaService';
import { useResumoRetomada } from '@/hooks/dashboard/useRetomadaDashboard';
import { APRESENTACAO_GRUPOS, resumirOntem, totalPendentes, type GrupoRetomada } from '@/features/retomada/regras';
import { RetomadaListaModal } from './RetomadaListaModal';
import { cn } from '@/lib/utils';

interface CartaoRetomadaDashboardProps {
  clinicaId?: string | null;
  adminMode?: boolean;
}

// Leitores de tela só ouvem o rótulo do botão, não o texto visual: os já retomados entram nele também.
function descreverBotaoGrupo(nome: string, grupo: GrupoResumoRetomada): string {
  const retomados = grupo.retomados > 0
    ? `, ${grupo.retomados} já retomado${grupo.retomados === 1 ? '' : 's'} hoje`
    : '';
  return `${nome}: ${grupo.pendentes} para retomar${retomados}. Abrir lista`;
}

export function CartaoRetomadaDashboard({ clinicaId, adminMode = false }: CartaoRetomadaDashboardProps) {
  const resumo = useResumoRetomada(clinicaId);
  const [grupoAberto, setGrupoAberto] = useState<GrupoRetomada | null>(null);

  // Admin sem clínica escolhida: o Dashboard já mostra o aviso para selecionar uma.
  if (!clinicaId) return null;

  const dados = resumo.data;
  const ninguem = !!dados && dados.grupos.every((grupo) => grupo.total === 0);

  return (
    <section aria-labelledby="retomada-titulo" className="rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 id="retomada-titulo" className="text-base font-semibold text-foreground">Leads para retomar hoje</h2>
            {dados && !ninguem && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-foreground">
                {totalPendentes(dados.grupos)} pendentes
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">Conta pelo dia do calendário. Promoção em massa não conta como retomada.</p>
        </div>
        {dados && <p className="text-xs font-medium text-muted-foreground">{resumirOntem(dados.ontemResultado)}</p>}
      </div>

      {resumo.isLoading ? (
        <div role="status" className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((indice) => (
            <div key={indice} className="h-24 animate-pulse rounded-xl bg-muted motion-reduce:animate-none" />
          ))}
          <span className="sr-only">Carregando leads para retomar</span>
        </div>
      ) : resumo.error && !dados ? (
        // Erro sem nenhum número guardado: não há o que mostrar, então o bloco de erro ocupa o cartão.
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <p role="alert" className="text-sm text-muted-foreground">
            {resumo.error instanceof Error ? resumo.error.message : 'Não foi possível carregar os leads para retomar.'}
          </p>
          <button
            type="button"
            onClick={() => void resumo.refetch()}
            className="min-h-11 rounded-full border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
          >
            Tentar novamente
          </button>
        </div>
      ) : ninguem ? (
        <p role="status" className="mt-4 text-sm text-muted-foreground">Ninguém para retomar hoje. As conversas dos últimos 7 dias estão em dia.</p>
      ) : dados ? (
        <ul className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {dados.grupos.map((grupo) => {
            const apresentacao = APRESENTACAO_GRUPOS[grupo.grupo];
            return (
              <li key={grupo.grupo}>
                <button
                  type="button"
                  onClick={() => setGrupoAberto(grupo.grupo)}
                  aria-label={descreverBotaoGrupo(apresentacao.nome, grupo)}
                  className={cn(
                    'flex h-full w-full flex-col rounded-xl p-3 text-left transition-[transform,opacity] duration-150 hover:opacity-90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                    apresentacao.classeCartao,
                  )}
                >
                  <span className="text-xs font-medium">{apresentacao.nome}</span>
                  <span className="mt-2 text-2xl font-semibold tabular-nums">{grupo.pendentes}</span>
                  <span className="mt-0.5 text-xs opacity-80">
                    {grupo.retomados > 0 ? `${grupo.retomados} já retomado${grupo.retomados === 1 ? '' : 's'} hoje` : 'para retomar'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* Erro com números antigos na tela: mantém os grupos e avisa, em vez de apagar tudo por uma falha de atualização. */}
      {resumo.error && dados && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <p role="status" className="text-xs text-muted-foreground">Não foi possível atualizar agora. Mostrando os últimos números.</p>
          <button
            type="button"
            onClick={() => void resumo.refetch()}
            className="min-h-11 rounded-full px-3 text-xs font-medium text-foreground underline-offset-2 transition-colors hover:bg-muted hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
          >
            Tentar novamente
          </button>
        </div>
      )}

      <RetomadaListaModal
        aberto={grupoAberto !== null}
        grupoInicial={grupoAberto ?? 'esperando_resposta'}
        onFechar={() => setGrupoAberto(null)}
        clinicaId={clinicaId}
        adminMode={adminMode}
      />
    </section>
  );
}
